/* eslint-disable @typescript-eslint/no-unused-vars */
import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "./RootStore";
import {
  ExternalWalletBalance,
  PaymentCard,
  Player,
  PlayerPrices,
  ProductDescription,
  ProductsServices,
} from "../apis/types";
import { BookingPaymentType } from "../utils/data";
import { CourtBookingApi } from "../apis/bookingapi";
import { Strings } from "../utils/strings";
import { HostedFormDispatchDataResponse } from "react-acceptjs";
import { detectCardTypeFromBIN } from "../utils/utils";

export class PaymentStore {
  rootStore: RootStore;
  selectedPlayersToPay: Player[] = [];
  // productServicesResponse: ProductsServices[] = [];
  totalAmount: number = 0;
  isPaymentSummaryReviewed: boolean = false;
  paymentCards: PaymentCard[] = [];
  defaultPaymentCard?: PaymentCard;
  isPaymentCardLoading: boolean = false;
  isChangePaymentMethodModalVisible: boolean = false;
  playerPricesMap: Map<number, ProductDescription> = new Map();
  selectedPaymentMethod: BookingPaymentType = BookingPaymentType.CARD;
  isAddCardSheetVisible: boolean = false;
  isPlayerPricesListLoading: boolean = false;
  extWalletBalance?: ExternalWalletBalance = undefined;
  payMyPartAmount: number = 0;

  // Reset all store values to their initial state
  resetStore = () => {
    this.selectedPlayersToPay = [];
    // this.productServicesResponse = [];
    this.totalAmount = 0;
    this.isPaymentSummaryReviewed = false;
    this.playerPricesMap.clear();
    this.isPlayerPricesListLoading = false;
    this.extWalletBalance = undefined;
    this.payMyPartAmount = 0;
    // Re-initialize with current user
    this.initializePlayersToPayListWithCurrentUser();
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.initializePlayersToPayListWithCurrentUser();

    reaction(
      () => ({
        selectedPlayers: this.rootStore.selectCircleStore.selectedPlayers,
        playerPrice: this.playerPricesMap.size,
      }),
      async (current, previous) => {
        this.syncSelectedPlayers();
        this.calcuateTotalAmount();
      }
    );

    reaction(
      () => ({
        selectedPlayersToPay: this.selectedPlayersToPay.length,
      }),
      async (current, previous) => {
        this.calculatePayMyPartAmount();
      }
    );

    reaction(
      () => ({
        timeSlots: this.rootStore.courtBookingStore.selectedTimeSlots,
        playerLength: this.rootStore.selectCircleStore.selectedPlayers.length,
      }),
      async (current, previous) => {
        this.isPaymentSummaryReviewed = false; // Reset this flag when players are changed
        if (
          current.timeSlots.length &&
          current.playerLength ===
            this.rootStore.courtBookingStore.totalPlayersToSelect
        ) {
          this.playerPricesMap.clear();
          // await this.fetchProductsAndServices();
          await this.getPlayersPaymentList();
          this.calcuateTotalAmount();
        }
      }
    );
  }

  setSelectedPaymentMethod = (value: BookingPaymentType) => {
    this.selectedPaymentMethod = value;
  };

  isPaymentThroughWallet = () => {
    const eWalletBalance =
      this.rootStore.userStore.userModel?.eWalletBalance ?? 0;
    return eWalletBalance > 0 && eWalletBalance > this.totalAmount;
  };

  isPaymentThroughExternalWallet = () => {
    const extWalletBalance = this.extWalletBalance?.balance ?? 0;
    return extWalletBalance > 0 && extWalletBalance >= this.payMyPartAmount;
  };

  setIsAddCardSheetVisible = (visible: boolean) => {
    this.isAddCardSheetVisible = visible;
  };

  calculatePayMyPartAmount = () => {
    const myPart = this.selectedPlayersToPay.reduce((acc, player) => {
      const amount = this.getPlayerAmount(player);
      acc += amount ?? 0;
      return acc;
    }, 0);
    this.payMyPartAmount = myPart;
  };

  getPlayersPaymentList = async () => {
    try {
      this.isPlayerPricesListLoading = true;
      const slots = this.rootStore.courtBookingStore.selectedTimeSlots;
      const courtId = this.rootStore.courtBookingStore.selectedCourt;
      const selectedPlayers = this.rootStore.selectCircleStore.selectedPlayers;
      const response = await CourtBookingApi.getPlayerPricesList(
        slots[0].startSlotTime,
        slots[slots.length - 1].endSlotTime,
        courtId.id,
        selectedPlayers?.[0]?.userId ?? 0,
        selectedPlayers?.[1]?.userId ?? 0,
        selectedPlayers?.[2]?.userId ?? 0
      );
      response.playerPrices?.forEach((price) => {
        this.playerPricesMap.set(price.playerId ?? 0, price.product);
      });
    } catch (err: any) {
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children:
          err.message ??
          "Some error occurred while getting players payment list. Please try again.",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    } finally {
      this.isPlayerPricesListLoading = false;
    }
  };

  fetchAllCards = async () => {
    try {
      const response = await CourtBookingApi.getPaymentMethod();
      this.paymentCards = response;
      this.defaultPaymentCard = response.find((p) => p.isDefault);
      this.setSelectedPaymentMethod(BookingPaymentType.CARD);
    } catch (err) {
      console.log(err);
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: "Error occurred while fetching cards list",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    }
  };

  fetchExternalWalletBalance = async () => {
    try {
      const response = await CourtBookingApi.getExternalWalletBalance();
      this.extWalletBalance = response;
    } catch (err) {
      console.log(err);
    }
  };

  // fetchProductsAndServices = async () => {
  //   try {
  //     const slots = this.rootStore.courtBookingStore.selectedTimeSlots;
  //     const courtId = this.rootStore.courtBookingStore.selectedCourt;
  //     const response = await CourtBookingApi.getProductAndServices(
  //       slots[0].startSlotTime,
  //       slots[slots.length - 1].endSlotTime,
  //       courtId.id
  //     );
  //     this.productServicesResponse = response;
  //   } catch (err) {
  //     console.log(err);
  //     this.rootStore.courtBookingStore.modalData = {
  //       visible: true,
  //       title: Strings.WARNING,
  //       children: "Error occurred while fetching booking fees.",
  //       btnText: Strings.OK,
  //       btnVariant: "outline-danger",
  //       onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
  //     };
  //     this.productServicesResponse = [];
  //   } finally {
  //   }
  // };

  initializePlayersToPayListWithCurrentUser = () => {
    const userModel = this.rootStore?.userStore?.userModel;
    if (userModel) {
      const currentUserAsPlayer = {
        userId: parseInt(userModel?.userId ?? "0", 10) ?? -1,
        name: userModel?.name ?? "",
        userImageUrl: userModel?.userImageUrl ?? "",
        email: userModel?.email ?? "",
        clubRank: 0,
        matchesWon: 0,
        userRating: 0,
        count: 0,
        lastMatchDate: "",
      };
      this.selectedPlayersToPay = [currentUserAsPlayer];
    }
  };

  // Helper method to sync selected players with payment list
  syncSelectedPlayers = () => {
    const updatedList: Player[] = [];

    this.rootStore.selectCircleStore
      .adjustAndGetSelectedPlayersList()
      .forEach((player) => {
        if (this.selectedPlayersToPay.some((p) => p.userId === player.userId)) {
          updatedList.push(player);
        }
      });

    this.selectedPlayersToPay = updatedList;
  };
  getIsPayForAll = () => {
    return (
      this.selectedPlayersToPay.length ===
      this.rootStore.selectCircleStore.adjustAndGetSelectedPlayersList().length
    );
  };

  setIsPayForAll = (value: boolean) => {
    if (value) {
      // If setting to true, get all players including user and guest players
      this.selectedPlayersToPay =
        this.rootStore.selectCircleStore.adjustAndGetSelectedPlayersList();
    } else {
      this.initializePlayersToPayListWithCurrentUser();
    }
  };

  togglePlayerToPay = (player: Player) => {
    const playerIndex = this.selectedPlayersToPay.findIndex(
      (p) => p.userId === player.userId
    );

    if (playerIndex > -1) {
      // If player is already in the list and we're in payForAll mode
      if (this.getIsPayForAll()) {
        // Set payForAll to false and remove only this player
        const allPlayersExceptThis = this.selectedPlayersToPay.filter(
          (p) => p.userId !== player.userId
        );
        this.selectedPlayersToPay = allPlayersExceptThis;
      } else {
        // Normal case - just remove the player
        this.removePlayerToPay(player.userId);
      }
    } else {
      // Add player if not in the array
      this.addPlayerToPay(player);
    }
  };

  isPlayerIncludedForPayment = (player: Player) => {
    return (
      this.selectedPlayersToPay.find(
        (p) => p.userId.toString() === player?.userId.toString()
      ) !== undefined
    );
  };

  getPlayerAmount = (player: Player) => {
    const playerPrice = this.playerPricesMap.get(player.userId);
    return playerPrice?.totalPrice ?? 0;
  };

  calcuateTotalAmount = () => {
    let total = 0;
    // const booking = this.productServicesResponse.find(
    //   (p) => p.categoryId === BOOKING_FEES_SERVICE_ID
    // );
    // this.rootStore.selectCircleStore
    //   .adjustAndGetSelectedPlayersList()
    //   .forEach((player) => {
    //     // if (!player.isGuestPlayer) {
    //     total += booking?.products?.[0]?.totalSalePrice ?? 0;
    //     // }
    //   });
    this.playerPricesMap.forEach((value, key) => {
      total += value.totalPrice ?? 0;
    });
    this.totalAmount = total;
  };

  addPlayerToPay = (player: Player) => {
    // Check if player is already in the array
    const playerExists = this.selectedPlayersToPay.some(
      (p) => p.userId === player.userId
    );

    // Only add if player doesn't already exist in the array
    if (!playerExists) {
      this.selectedPlayersToPay = [...this.selectedPlayersToPay, player];
      // If we've reached the total number of players, set payForAll to true
      if (
        this.selectedPlayersToPay.length ===
        this.rootStore.courtBookingStore.totalPlayersInMatch
      ) {
        this.setIsPayForAll(true);
      }
    }
  };

  removePlayerToPay = (playerId: number) => {
    this.selectedPlayersToPay = this.selectedPlayersToPay.filter(
      (player) => player.userId !== playerId
    );
  };

  changeDefaultPaymentCard = async (cardId: number) => {
    if (cardId === this.defaultPaymentCard?.id) {
      return;
    }

    try {
      this.isPaymentCardLoading = true;
      const response = await CourtBookingApi.setDefaultPaymentMethod(cardId);
      if (response.isSuccess) {
        this.defaultPaymentCard = response.data;
        this.isChangePaymentMethodModalVisible = false;
        this.setSelectedPaymentMethod(BookingPaymentType.CARD);
      } else {
        this.rootStore.courtBookingStore.modalData = {
          visible: true,
          title: Strings.WARNING,
          children:
            response.message ?? "Error occurred while changing default card.",
          btnText: Strings.OK,
          btnVariant: "outline-danger",
          onClose: () =>
            (this.rootStore.courtBookingStore.modalData = undefined),
        };
      }
    } catch (err: any) {
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: err.message ?? "Some error occurred. Please try again",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    } finally {
      this.isPaymentCardLoading = false;
    }
  };

  addNewPaymentCard = async (token: string, zipCode: string) => {
    try {
      // const cardType = detectCardTypeFromBIN(data.encryptedCardData.bin);
      this.isPaymentCardLoading = true;
      const response = await CourtBookingApi.postPaymentMethod({
        token: token,
        // last4: data.encryptedCardData.cardNumber.slice(-4),
        // validUntil: data.encryptedCardData.expDate,
        // cardBrand: cardType,
        zip: zipCode.substring(0, 5),
      });
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.SUCCESS,
        children: response.message ?? "Card Added Successfully",
        btnText: Strings.OK,
        onClose: async () => {
          this.rootStore.courtBookingStore.modalData = undefined;
          this.setIsAddCardSheetVisible(false);
          await this.fetchAllCards();
        },
      };
    } catch (err: any) {
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: err.message ?? "Some error occurred. Please try again",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    } finally {
      this.isPaymentCardLoading = false;
    }
  };
}
